import {
  AfterContentInit,
  Component,
  computed,
  contentChild,
  ContentChild,
  ContentChildren,
  inject,
  input,
  Input,
  OnDestroy,
  OnInit,
  QueryList,
  signal
} from "@angular/core";
import { PageGenericActionItem } from "./page-generic-action-item";
import { PageGenericActionSeparatorComponent } from "./page-generic-action-separator.component";
import { PageGenericActionComponent } from "./page-generic-action.component";
import { PageGenericSearchComponent } from "./page-generic-search.component";
import { PageGenericToolbarCustomItemDirective } from "./page-generic-toolbar-custom-item.directive";
import { PageToolbarComponent } from "src/app/components/ui/page/page-shared/page-toolbar/page-toolbar.component";
import { IntegrationService } from "src/app/services/integration.service";
import { EventsEmitter, EventsUnsubscriber } from "src/app/support/events";
import { groupBy, toPairs } from "lodash-es";
import {
  IntegrationDropPoints,
  IntegrationLayoutAction,
  IntegrationLayoutActionRendered
} from "src/app/integrations/integration-layout";
import { INTEGRATION_EVENTS } from "src/app/integrations/integration-events";
import {
  IntegrationState,
  integrationStateDefault
} from "src/app/integrations/integration-state";
import { PLATFORM_TOKEN } from "src/app/tokens/platform.token";
import { PlatformType } from "src/app/common/platform-type";

@Component({
  selector: "snoots-page-generic",
  templateUrl: "page-generic.component.html",
  styles: [
    `
      :host {
        display: flex;
        flex: 1;
      }
    `
  ],
  standalone: false
})
export class PageGenericComponent
  implements OnInit, AfterContentInit, OnDestroy
{
  @ContentChildren(PageGenericToolbarCustomItemDirective)
  customItems!: QueryList<PageGenericToolbarCustomItemDirective>;

  toolbarContent = contentChild(PageToolbarComponent);
  toolbarInput = input<PageToolbarComponent | undefined>();

  toolbar = computed<PageToolbarComponent | undefined>(() => {
    const _toolbarContent: PageToolbarComponent | undefined =
      this.toolbarContent();
    const _toolbarInput: PageToolbarComponent | undefined = this.toolbarInput();

    return _toolbarContent || _toolbarInput;
  });

  toolbarIntegrations = signal<
    Array<{
      title: string;
      icon: string;
      actionsRendered: Array<{
        action: IntegrationLayoutAction;
        rendered: IntegrationLayoutActionRendered;
      }>;
    }>
  >([]);

  customItemsStart: PageGenericToolbarCustomItemDirective[] = [];
  customItemsEnd: PageGenericToolbarCustomItemDirective[] = [];

  @ContentChildren(PageGenericActionItem)
  actions!: QueryList<
    PageGenericActionComponent | PageGenericActionSeparatorComponent
  >;

  @ContentChild(PageGenericSearchComponent)
  search: PageGenericSearchComponent | null = null;

  @Input()
  title!: string;

  @Input()
  noPadding: boolean = true;

  @Input()
  layout: "default" | "center" = "default";

  @Input()
  bgClasses: string | undefined;

  disableAbsolute = input<boolean>(false);

  readonly integrationDropPoint = input<IntegrationDropPoints | null>(null);
  readonly integrationEntity = input<any | null>(null);
  readonly integrationParamsBuilder = input<
    (() => Record<string, unknown>) | null
  >(null);

  @Input()
  eventsEmitter: EventsEmitter | undefined;

  noMargin = input<boolean>(false);
  noBackground = input<boolean>(false);

  defaultActions: PageGenericActionComponent[] = [];
  primaryActions: Array<
    PageGenericActionComponent | PageGenericActionSeparatorComponent
  > = [];

  protected readonly PLATFORM = inject(PLATFORM_TOKEN);
  protected readonly PlatformType = PlatformType;

  private integrationActions: IntegrationLayoutAction[] = [];

  private readonly integrationService = inject(IntegrationService);

  private eventsEmitterUnsubscribe: EventsUnsubscriber | undefined;

  ngOnInit(): void {
    if (this.eventsEmitter) {
      this.eventsEmitterUnsubscribe = this.eventsEmitter.on(
        INTEGRATION_EVENTS.PAGE_SELECTION_CHANGED,
        (keys) => {
          const entity = this.integrationEntity();

          this.renderIntegrations({
            entity,
            selectedEntitiesIds: keys
          });
        }
      );
    }
  }

  ngAfterContentInit(): void {
    this.primaryActions = this.actions.toArray();

    this.customItemsStart = this.customItems
      .toArray()
      .filter((x) => x.position === "start");
    this.customItemsEnd = this.customItems
      .toArray()
      .filter((x) => x.position === "end");

    this.loadIntegrations(this.integrationDropPoint());
    this.renderIntegrations({
      ...integrationStateDefault(),
      entity: this.integrationEntity()
    });
  }

  ngOnDestroy(): void {
    if (this.eventsEmitterUnsubscribe) {
      this.eventsEmitterUnsubscribe();
    }
  }

  private loadIntegrations(
    integrationDropPoint: IntegrationDropPoints | null
  ): void {
    if (!integrationDropPoint) {
      return;
    }

    this.integrationActions =
      this.integrationService.getActionsByDropPoint(integrationDropPoint);
  }

  private renderIntegrations(state: IntegrationState): void {
    const rendered = this.integrationActions.map(
      (action: IntegrationLayoutAction) => {
        return {
          action,
          rendered: action.render(state)
        };
      }
    );

    const grouped = groupBy(rendered, (x) => {
      return x.action.integrationName;
    });

    this.toolbarIntegrations.set(
      toPairs(grouped).map(([key, value]) => {
        const integration = this.integrationService.getById(
          value[0].action.installation!.integrationId
        );

        return {
          title: key,
          icon: integration.icon!,
          actionsRendered: value.map((x) => {
            return {
              action: x.action,
              rendered: x.rendered
            };
          })
        };
      })
    );
  }

  isAction(
    item: PageGenericActionComponent | PageGenericActionSeparatorComponent
  ): boolean {
    return !!(<PageGenericActionComponent>item).icon;
  }

  isSeparator(
    item: PageGenericActionComponent | PageGenericActionSeparatorComponent
  ): boolean {
    return !(<PageGenericActionComponent>item).icon;
  }
}
